import { useEffect, useLayoutEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import {
  leaveGame,
  selectGame,
  selectViewerId,
  selectWinnerTeam,
  selectWinnerUser,
} from "src/store/game"
import { enemyTally, finalStandingFrom, roundsSurvived, survivalOrder } from "src/utils/finalStanding"
import { isSurvival } from "src/utils/gameModes"
import { getPlace } from "src/utils/maps"
import { raceNames } from "src/utils/races"
import { DEFAULT_AVATAR, seatPictureFor } from "src/utils/cosmetics"
import { leagueByPoints } from "src/utils/ranking"
import { selectRankingLeagues } from "src/store/common"
import { selectMyAvatar } from "src/store/economy"
import { selectUserId } from "src/store/user"

import ModalService from "src/modals"
import { avatars } from "src/assets/images"
import LeagueFrame from "src/components/LeagueFrame"
import PlayerCardButton from "src/components/PlayerCardButton"
import Modal from "src/components/modals/Modal"
import "./GameOverModal.css"
import { playUiSound } from "src/utils/audio"

// "2 851" rather than "2851": the same grouping the rest of the game writes scores in. The gap is a
// no-break space, because a plain one is a place a line may break — and in a fixed column "123 456"
// did exactly that, the thousands on one line and the rest under them (owner, 2026-09-20).
const grouped = (value) => (value == null ? "—" : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0"))

const GameOverModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const game = useSelector(selectGame)
  const winnerTeam = useSelector(selectWinnerTeam)
  const winnerUser = useSelector(selectWinnerUser)
  const userId = useSelector(selectViewerId)
  const leagues = useSelector(selectRankingLeagues)
  // Your own picture off the wallet, which the shop updates the moment you choose one; the game
  // payload carries the one you wore when it was last sent. The account's id, not the viewer's:
  // a replay watched through another seat must not paint your picture on it.
  const accountId = useSelector(selectUserId)
  const myAvatar = useSelector(selectMyAvatar)
  /* Whether the full table fits its box (owner, 2026-10-08). A fixed width cannot answer it — the
     room it needs depends on the names and on the numbers — so the table is measured on every
     resize of its box: laid out full for one synchronous read (the browser paints nothing in
     between), and stacked whenever that is wider than the box. */
  // A callback ref, not `useRef`: the modal can render its table more than once, and an observer
  // left on a replaced element never fires again.
  const [table, setTable] = useState(null)
  const [stacked, setStacked] = useState(false)
  useLayoutEffect(() => {
    if (!table || typeof ResizeObserver === "undefined") return undefined
    const measure = () => {
      const wasStacked = table.classList.contains("stacked")
      table.classList.remove("stacked")
      const tooWide = table.scrollWidth > table.clientWidth + 1
      if (wasStacked) table.classList.add("stacked")
      setStacked(tooWide)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(table)
    return () => observer.disconnect()
  }, [table])
  const [steps, setSteps] = useState(null)
  // The game's last tick as the steps endpoint reports it. In review the store's game is the
  // *step being watched*, so `game.tick` reads 0 on the opening board — and "outlasted everybody
  // · 0 rounds" over a 24-round game was what the RESULT button showed (game 3510, 2026-09-16).
  const [finalTick, setFinalTick] = useState(null)
  // What the columns mean (S25): four numbers a player has never been told the meaning of.
  const [showHelp, setShowHelp] = useState(false)

  const gameId = game?.id

  // This screen counted cells out of the live metadata, and a finished game has already released
  // the cells of everyone eliminated — so the losing side was reported as holding zero cities,
  // which is true of the moment and useless as a result. Their last recorded tick knows better,
  // and `GET /game/{id}/steps/` reveals every player's summary once the game is FINISHED (08/D3a).
  useEffect(() => {
    if (!gameId) return
    let cancelled = false
    apiClient
      .get(`/game/${gameId}/steps/`)
      .then((response) => {
        if (cancelled) return
        setSteps(response.data?.steps || [])
        setFinalTick(response.data?.tick ?? null)
      })
      // The result is not worth blocking on history. Falling back to the live count is exactly
      // what this screen did before the endpoint existed.
      .catch(() => !cancelled && setSteps([]))
    return () => {
      cancelled = true
    }
  }, [gameId])

  /* **In survival the verdict is the player's own** (SV16). Everybody is on one side, so the
     winning team says nothing about whether *you* won it: `outcome` on the seat is the server's
     per-player answer — win for the survivor, draw for everybody who fell on the ending round,
     loss for anybody who fell before them. A classic game is untouched and still reads the side. */
  const survival = isSurvival(game?.mode)
  const myOutcome = game?.players?.find((player) => player.user.id === userId)?.outcome ?? null

  /* The verdict, once. Keyed on the game rather than on mount so a re-render cannot double it, and
     it deliberately says nothing for a spectator: they have no side, which is the whole of the
     headline above. `game` may be null on the first pass, which is why the hook cannot sit below
     the early return. */
  const winner = game && winnerTeam
  const mySide = game?.players?.find((player) => player.user.id === userId)?.team
  // No winner on a finished game is a draw (owner's R4): both sides lost their last crowns in
  // the same round. In survival there is no winning side to be missing — the draw is nobody
  // outlasting anybody — so the absent thing is the winner's name. Neither fanfare fits a draw,
  // so neither plays.
  const isDraw =
    Boolean(game) && game.status === "finished" && !(survival ? winnerUser : winnerTeam)
  const cue = survival
    ? myOutcome === "win"
      ? "victory"
      : myOutcome === "loss"
        ? "defeat"
        : null
    : winner && mySide
      ? mySide === winner
        ? "victory"
        : "defeat"
      : null
  useEffect(() => {
    if (cue) playUiSound(cue)
  }, [cue])

  if (!game) return null

  /* A spectator has no player row, and so no side and no verdict. Without this they were told
     "DEFEAT" in the largest type on the screen for a game between two bots they were only holding
     the clock for — the verdict below reads as a loss for them the same way it does for a loser.
     The rest of the screen is the same result either way, so the only thing that changes is the
     headline: theirs is the outcome ("Zombies hold the field", or in survival the name of whoever
     outlasted everybody) rather than a judgement on them. */
  const me = game.players.find((player) => player.user.id === userId)
  const watching = !me
  const rounds = Math.max(game.tick ?? 0, finalTick ?? 0)

  /* Which of the three words goes in the largest type on the screen. In survival it is the seat's
     own `outcome`; in a classic game it is the side that took the field, as it always was. The
     fallback for a survival seat with no outcome on it — a game that has ended without the column
     ever being written — is the draw/loss the rest of the screen already reads. */
  const verdict = survival
    ? myOutcome || (isDraw ? "draw" : "loss")
    : isDraw
      ? "draw"
      : me && winnerTeam && me.team === winnerTeam
        ? "win"
        : "loss"

  /* The line above the headline, and the whole headline for somebody who was only watching.

     Survival replaces the side that held the field with the person who outlasted everybody,
     because "the humans hold the field" is true of every survival game the people win and tells a
     reader nothing. The round count comes off the game's own tick — the same number the subtitle
     prints — so nothing here invents a figure. */
  const result = survival
    ? winnerUser
      ? t("gameOver.survivor", { name: winnerUser.username, count: rounds })
      : isDraw
        ? t("gameOver.everybodyFell", { round: rounds })
        : t("gameOver.gameEnded")
    : winnerTeam
      ? winnerTeam === "human"
        ? t("gameOver.humansHoldTheField")
        : t("gameOver.zombiesHoldTheField")
      : isDraw
        ? t("gameOver.bothFell")
        : t("gameOver.gameEnded")

  const standing = finalStandingFrom({ steps, players: game.players })
  const recorded = Object.fromEntries(standing.map((row) => [row.userId, row]))

  // Live fallback: used until the history arrives, and for any player it has no record of.
  const cellsPerUser = {}
  Object.values(game.metadata?.cells || {}).forEach((cell) => {
    if (cell.owner != null) cellsPerUser[cell.owner] = (cellsPerUser[cell.owner] || 0) + 1
  })

  /* `hideAll` is the load-bearing line, and its absence is why this button looked dead (owner's
     report, 2026-09-04). The modal host renders outside the routes, so a modal survives a route
     change: the click emptied the store and navigated home, and the game-over screen stayed on
     screen over the top of it, unchanged. Nothing had failed — the one thing the player could see
     was the one thing that had not moved. `watchReplay` below always did this; the two exits
     simply did not agree. */
  const goHome = () => {
    dispatch(leaveGame())
    ModalService.hideAll()
    navigate("/")
  }

  // "Revancha" tried to open the create-lobby dialog across a route change and never survived the
  // trip (owner's P25). What a finished game can actually offer is its own story: the replay —
  // which is the view behind this modal (owner, 2026-09-07): a finished game flips into review
  // mode in place, stepper and seat picker in the bar, so watching it is closing this. The bar's
  // RESULT button brings the standings back.
  const watchReplay = () => ModalService.hideAll()

  const initialLocation = me?.initialLocations?.[0]
  const operationName = initialLocation
    ? getPlace({ column: initialLocation.column, row: initialLocation.row }).name
    : null

  const subtitleParts = []
  if (operationName) subtitleParts.push(t("gameOver.operation", { name: operationName }))
  subtitleParts.push(t("gameOver.rounds", { count: rounds }))

  /* **The standings are the people** (SV16). A survival game seats a bot every ten rounds, so its
     `players` list is a roster of strangers nobody chose and by round sixty there are more of them
     than this table was built to draw. They are counted into one line under the rows instead; the
     people keep a row each. A classic game's list is every seat, unchanged. */
  const people = survival ? game.players.filter((player) => !player.user.isBot) : game.players
  const enemies = survival ? enemyTally(game.players) : { total: 0, fallen: 0 }

  const rows = people.map((player) => {
    const record = recorded[player.user.id]
    return {
      player,
      // How long they lasted: the game's own length for whoever was still standing at the end,
      // and the round their last crown fell for everybody else. Survival's only column.
      survived: survival ? roundsSurvived(player, rounds) : null,
      cities: record?.cities ?? cellsPerUser[player.user.id] ?? 0,
      // The server's own scores (S25): the value of the army held, on its cross-team scale; every
      // building level; the value of the enemy units killed. A summary from before the field
      // existed has no `units_score`, and then the headcount is the closest honest figure.
      army: record?.armyScore ?? record?.army,
      buildings: record?.buildingsScore,
      kills: record?.killsScore,
      // What this game paid the seat, from the server's own rule — not the account's running
      // total, which said nothing about this game and read a red 0 to somebody who had just lost.
      points: player.rankingDelta ?? null,
      isYou: player.user.id === userId,
      // The winner's row is marked. In survival that is the one person the server named, not
      // everybody on the winning side — which in survival is everybody.
      isWinner: survival ? player.outcome === "win" : Boolean(winnerTeam && player.team === winnerTeam),
      // What `survivalOrder` sorts on: null while a seat still stood at the end.
      eliminatedAtTick: player.eliminatedAtTick ?? null,
    }
  })
  // Survivors first, then the fallen from the last round backwards: in survival the order *is*
  // the result. A classic game keeps the server's seat order, where the teams are the story.
  const orderedRows = survival ? survivalOrder(rows) : rows
  const figure = (value) => (value == null ? "—" : grouped(value))
  /* Did this game move the player across a league boundary? Read off the points before (now less
     what the game paid) and after, by points alone: Commander depends on everybody's places. */
  const leagueMove = (player, delta) => {
    if (player.user.isBot || delta == null || delta === 0) return null
    const after = leagueByPoints(player.user.rankingPoints, leagues)
    const before = leagueByPoints((player.user.rankingPoints ?? 0) - delta, leagues)
    if (!after || !before || after === before) return null
    return { way: delta > 0 ? "up" : "down", to: after }
  }
  // A delta reads with its sign: "+25", "−10", and a plain 0 for a draw that cost nothing.
  const signed = (value) =>
    value == null ? "—" : value > 0 ? `+${grouped(value)}` : value < 0 ? `−${grouped(-value)}` : "0"
  const columns = [
    ...(survival ? [{ key: "survived", label: t("gameOver.roundsSurvived") }] : []),
    { key: "cities", label: t("gameOver.cities") },
    { key: "army", label: t("gameOver.army"), help: t("gameOver.helpArmy") },
    { key: "buildings", label: t("gameOver.buildings"), help: t("gameOver.helpBuildings") },
    { key: "kills", label: t("gameOver.kills"), help: t("gameOver.helpKills") },
    { key: "points", label: t("gameOver.points"), help: t("gameOver.helpPoints") },
  ]

  /* Why the points column did not move. Survival is ranked from two people up (SV15) — the bots
     are the arena, not the opponent anybody is measured against — so a solo game is the practice
     case there, and a classic game is one the moment any bot is seated. Read off the data rather
     than off a flag, so the note and the server's rule cannot drift. */
  const unranked =
    game.ranked != null
      ? !game.ranked
      : survival
        ? people.length < 2
        : game.players.some((seat) => seat.user.isBot)

  return (
    <Modal width="62%" top="8vh" maxHeight="88vh">
      <div
        className={`game-over ${watching ? "watched" : verdict === "draw" ? "drawn" : verdict === "win" ? "won" : "lost"}`}
      >
        <header className="game-over-head">
          {/* Watching: the result *is* the headline, so the small line above it would say it
              twice. */}
          {!watching && <p className="game-over-verdict">{result}</p>}
          <h1 className="game-over-title">
            {watching
              ? result
              : verdict === "draw"
                ? t("gameOver.draw")
                : verdict === "win"
                  ? t("gameOver.victory")
                  : t("gameOver.defeat")}
          </h1>
          <p className="game-over-sub">{subtitleParts.join(" · ")}</p>
        </header>

        <div
          ref={setTable}
          className={`standing${survival ? " survival" : ""}${stacked ? " stacked" : ""}`}
          style={{ "--figure-columns": columns.length }}
        >
          <div className="standing-head">
            <span className="section-label">
              {t("gameOver.player")}
              {/* Every figure column is a claim; the "?" opens what each one is. A header that is
                  a button and a legend below, rather than a tooltip: the phone shell has no hover
                  and the legend is what a player screenshots. */}
              <button
                type="button"
                className="standing-help-toggle"
                aria-expanded={showHelp}
                aria-label={t("gameOver.help")}
                title={t("gameOver.help")}
                onClick={() => setShowHelp((open) => !open)}
              >
                ?
              </button>
            </span>
            {columns.map((column) => (
              <span className="section-label" key={column.key} title={column.help}>
                {column.label}
              </span>
            ))}
          </div>
          {showHelp && (
            <ul className="standing-help">
              {columns
                .filter((column) => column.help)
                .map((column) => (
                  <li key={column.key}>{column.help}</li>
                ))}
            </ul>
          )}

          {orderedRows.map(({ player, isYou, isWinner, ...figures }) => (
            <div className={`standing-row${isYou ? " you" : ""}${isWinner ? " winner" : " beaten"}`} key={player.user.id}>
              <div className="standing-player">
                {/* The player's own picture, in the frame of their league (owner, 2026-10-08), larger
                    than the lobby's: this is the screen the game ends on. The person, not the map
                    colour they played in (owner, 2026-10-09) — the board is over. */}
                <PlayerCardButton
                  user={player.user}
                  picture={seatPictureFor(player, player.user.id === accountId ? myAvatar : null)}
                  className="standing-card-button"
                >
                  <LeagueFrame
                    league={player.user.league}
                    bot={Boolean(player.user.isBot ?? player.user.is_bot)}
                    className="standing-frame"
                  >
                    <span className="standing-look">
                      <img
                        src={avatars[seatPictureFor(player, player.user.id === accountId ? myAvatar : null)] || avatars[DEFAULT_AVATAR]}
                        alt=""
                      />
                    </span>
                  </LeagueFrame>
                </PlayerCardButton>
                <div>
                  <p className="standing-name">
                    {player.user.username}
                    {isYou ? <span className="standing-you"> · {t("gameOver.you")}</span> : null}
                  </p>
                  <p className="standing-meta">
                    {t(`gameOver.team.${player.team}`, { defaultValue: player.team })} ·{" "}
                    {raceNames[player.race] ? raceNames[player.race].toLowerCase() : t("gameOver.raceless")}
                  </p>
                  {leagueMove(player, figures.points) && (
                    <p className={`standing-league-move ${leagueMove(player, figures.points).way}`}>
                      <span aria-hidden="true">{leagueMove(player, figures.points).way === "up" ? "▲" : "▼"}</span>{" "}
                      {t(leagueMove(player, figures.points).way === "up" ? "gameOver.promoted" : "gameOver.demoted", {
                        league: t(`ranking.leagues.${leagueMove(player, figures.points).to}`),
                      })}
                    </p>
                  )}
                </div>
              </div>
              {/* Each figure carries its own label, hidden everywhere except the phone shell —
                  there the header row cannot sit above columns that are no longer columns, and an
                  unlabelled row of numbers says nothing. In the markup rather than in CSS because
                  a label is words, and words are translated. A column is blank rather than zero
                  when the history has no record of that player: "we were not told" and "they had
                  nothing left" are different claims. */}
              {columns.map((column) => (
                <span className={`standing-figure${column.key === "points" ? " points" : ""}`} key={column.key} title={column.help}>
                  <span className="standing-figure-label">{column.label}</span>
                  {column.key === "points" ? signed(figures[column.key]) : figure(figures[column.key])}
                </span>
              ))}
            </div>
          ))}

          {/* The waves, in one line under the people (SV16). They have no ranking points, no
              class worth a plate and no name anybody remembers — what a player wants to know is
              how many came and how many they put down. */}
          {survival && enemies.total > 0 && (
            <div className="standing-row enemies">
              <span className="standing-enemies">
                {t("gameOver.enemiesRow", { count: enemies.total, fallen: enemies.fallen })}
              </span>
            </div>
          )}
        </div>

        {/* The points column shows a running total, so in a practice game it shows a number that
            did not move and gives no reason. Said out loud instead: a game with a bot in it pays
            credits and no ranking (server rule, 2026-09-04), and a solo survival is the same case
            for the same reason (SV15) — a player who has just won one should learn that here
            rather than by comparing two screenshots of their profile. */}
        {unranked && <p className="game-over-unranked">{t("gameOver.unranked")}</p>}

        <footer className="game-over-foot">
          <button className="game-over-rematch" onClick={watchReplay}>
            {t("gameOver.watchReplay")}
          </button>
          {/* The end-of-game panels (owner, 2026-09-22). The steps this screen already fetched are
              handed straight over, so opening them costs no second request. */}
          <button
            className="quiet game-over-graphs"
            onClick={() => ModalService.show("GameGraphsModal", { steps, players: game.players })}
          >
            {t("graphs.see")}
          </button>
          <button className="quiet game-over-home" onClick={goHome}>
            {t("gameOver.backToBase")}
          </button>
        </footer>
      </div>
    </Modal>
  )
}

export default GameOverModal
